import type { ListParams } from '@nucleus-commerce/admin-kit/index-table';
import {
  deleteAdminV1StoresStoreIdCollectionsId,
  deleteAdminV1StoresStoreIdCollectionsIdProductsProductId,
  getAdminV1StoresStoreIdCollections,
  getAdminV1StoresStoreIdCollectionsId,
  getAdminV1StoresStoreIdCollectionsIdProducts,
  getAdminV1StoresStoreIdProductsProductIdCollections,
  patchAdminV1StoresStoreIdCollectionsId,
  postAdminV1StoresStoreIdCollections,
  postAdminV1StoresStoreIdCollectionsBulk,
  postAdminV1StoresStoreIdCollectionsIdProducts,
  postAdminV1StoresStoreIdCollectionsIdRefresh,
  postAdminV1StoresStoreIdCollectionsPreviewRules,
  putAdminV1StoresStoreIdCollectionsIdProducts,
  putAdminV1StoresStoreIdProductsProductIdCollections,
  type BulkCollectionsRequest,
  type BulkResponse,
  type Collection,
  type CollectionProductItem,
  type CollectionRulesInput,
  type GetAdminV1StoresStoreIdCollections200,
  type PatchAdminV1StoresStoreIdCollectionsIdBody,
  type PostAdminV1StoresStoreIdCollectionsBody,
  type PostAdminV1StoresStoreIdCollectionsIdProductsBody,
  type PreviewRulesResponse,
  type ProductCollectionsResponse,
} from '@nucleus-commerce/api-client/admin';

/**
 * List params as `listParams` builds them from the route's search: `q`, `sort`, the flat filter
 * params (`kind=manual`, `status=active,hidden`, `updatedAt_gte=2026-09-01`), `cursor`, `limit`.
 */
export type CollectionListParams = ListParams;

/** Collections of one store: manual lists and rule-driven (automated) ones. */
export const collectionsApi = {
  list: (
    storeId: string,
    params: CollectionListParams,
  ): Promise<GetAdminV1StoresStoreIdCollections200> =>
    getAdminV1StoresStoreIdCollections({ storeId }, params),
  get: (storeId: string, id: string): Promise<Collection> =>
    getAdminV1StoresStoreIdCollectionsId({ storeId, id }),
  create: (storeId: string, body: PostAdminV1StoresStoreIdCollectionsBody): Promise<Collection> =>
    postAdminV1StoresStoreIdCollections({ storeId }, body),
  update: (
    storeId: string,
    id: string,
    body: PatchAdminV1StoresStoreIdCollectionsIdBody,
  ): Promise<Collection> => patchAdminV1StoresStoreIdCollectionsId({ storeId, id }, body),
  remove: (storeId: string, id: string): Promise<void> =>
    deleteAdminV1StoresStoreIdCollectionsId({ storeId, id }),

  products: (storeId: string, id: string): Promise<CollectionProductItem[]> =>
    getAdminV1StoresStoreIdCollectionsIdProducts({ storeId, id }).then((r) => r.items),
  setProducts: (
    storeId: string,
    id: string,
    productIds: string[],
  ): Promise<CollectionProductItem[]> =>
    putAdminV1StoresStoreIdCollectionsIdProducts({ storeId, id }, { productIds }).then(
      (r) => r.items,
    ),

  /**
   * Appends to a manual list: master product ids (at most 2,000, the collection's cap) or a
   * store-scope selection; ids already there keep their place. Answers the whole list.
   */
  addProducts: (
    storeId: string,
    id: string,
    body: PostAdminV1StoresStoreIdCollectionsIdProductsBody,
  ): Promise<CollectionProductItem[]> =>
    postAdminV1StoresStoreIdCollectionsIdProducts({ storeId, id }, body).then((r) => r.items),
  removeProduct: (storeId: string, id: string, productId: string): Promise<void> =>
    deleteAdminV1StoresStoreIdCollectionsIdProductsProductId({ storeId, id, productId }),
  /** 200 when every id succeeded, 207 with per-id results otherwise; both resolve. */
  bulk: (storeId: string, body: BulkCollectionsRequest): Promise<BulkResponse> =>
    postAdminV1StoresStoreIdCollectionsBulk({ storeId }, body),

  /** Every collection of the store with this master product's membership. */
  forProduct: (storeId: string, productId: string): Promise<ProductCollectionsResponse> =>
    getAdminV1StoresStoreIdProductsProductIdCollections({ storeId, productId }),
  /** Sets the product's manual memberships; automated collections are left to their rules. */
  setForProduct: (
    storeId: string,
    productId: string,
    collectionIds: string[],
  ): Promise<ProductCollectionsResponse> =>
    putAdminV1StoresStoreIdProductsProductIdCollections({ storeId, productId }, { collectionIds }),

  previewRules: (storeId: string, rules: CollectionRulesInput): Promise<PreviewRulesResponse> =>
    postAdminV1StoresStoreIdCollectionsPreviewRules({ storeId }, { rules }),
  refresh: (storeId: string, id: string): Promise<Collection> =>
    postAdminV1StoresStoreIdCollectionsIdRefresh({ storeId, id }),
};
